import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Checkbox } from '@rentspree/component-2023.components.atoms.checkbox';

import { ScrollableContent } from '../../components/atoms/ScrollableContent';
import { SectionHeader } from '../../components/organisms/SectionHeader';
import { MultiStepLayout } from '../../components/templates/MultiStepLayout';
import { AgreementAcceptance } from '../../types/tenantScreening';

export interface AgreementStepProps {
  agreement: Partial<AgreementAcceptance>;
  onAgreementChange: (agreement: Partial<AgreementAcceptance>) => void;
  onNext: () => void;
  onBack: () => void;
  onHomeClick?: () => void;
  onExitClick?: () => void;
  isLoading?: boolean;
}

// Mock agreement content - will be fetched from API
const MOCK_AGREEMENT_CONTENT = `
TRANSUNION TENANT SCREENING SERVICES AGREEMENT

1. INTRODUCTION
This Agreement governs the use of TransUnion's tenant screening services. By accepting this agreement, you acknowledge that you have read, understood, and agree to be bound by the terms and conditions set forth herein.

2. PERMITTED USE
You may use TransUnion's tenant screening services only for the purpose of evaluating prospective tenants for rental properties. You must have a legitimate business need and lawful purpose for accessing tenant screening information.

3. COMPLIANCE WITH LAWS
You agree to comply with all applicable federal, state, and local laws, including but not limited to:
- Fair Credit Reporting Act (FCRA)
- Fair Housing Act
- Equal Credit Opportunity Act
- State and local tenant screening laws

4. APPLICANT CONSENT
Before obtaining a tenant screening report, you must:
- Obtain written consent from the applicant
- Provide required disclosures
- Ensure the applicant understands their rights

5. DATA PROTECTION
You agree to:
- Maintain the confidentiality of all screening information
- Use appropriate security measures to protect data
- Not share information with unauthorized parties
- Dispose of information securely when no longer needed

6. ADVERSE ACTION REQUIREMENTS
If you take adverse action based on information in a screening report, you must:
- Provide adverse action notice to the applicant
- Include required disclosures
- Inform the applicant of their right to dispute information

7. LIMITATIONS OF LIABILITY
TransUnion's liability is limited to the amount paid for the screening service. TransUnion makes no warranties regarding the accuracy or completeness of the information provided.

8. INDEMNIFICATION
You agree to indemnify and hold harmless TransUnion from any claims, damages, or expenses arising from your use of the screening services.

9. TERMINATION
This agreement may be terminated by either party with written notice. Upon termination, you must cease use of the services and destroy all confidential information.

10. GOVERNING LAW
This agreement is governed by the laws of the State of Illinois.

By checking the box below, you acknowledge that you have read, understood, and agree to be bound by the terms of this agreement.
`;

export const AgreementStep: React.FC<AgreementStepProps> = ({
  agreement,
  onAgreementChange,
  onNext,
  onBack,
  onHomeClick,
  onExitClick,
  isLoading = false,
}) => {
  const [isAccepted, setIsAccepted] = useState<boolean>(
    agreement.accepted || false
  );

  const handleAcceptanceChange = (accepted: boolean) => {
    setIsAccepted(accepted);
    onAgreementChange({
      accepted,
      timestamp: new Date(),
      version: '1.0',
    });
  };

  const handleNext = () => {
    if (isAccepted && !isLoading) {
      onNext();
    }
  };

  return (
    <MultiStepLayout
      currentStep={4}
      total={7}
      currentSubStepPercentage={100}
      onBack={onBack}
      onNext={handleNext}
      onHomeClick={onHomeClick}
      onExitClick={onExitClick}
      backDisabled={isLoading}
      nextDisabled={!isAccepted || isLoading}
      nextLabel={isLoading ? 'Processing...' : 'Accept & Continue'}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: 700, md: 800 },
          mx: 'auto',
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <SectionHeader
          title='TransUnion Service Agreement'
          description='Please review and accept the terms of service to continue with the tenant screening process.'
          align='center'
        />
        <ScrollableContent>
          <Typography
            variant='body1'
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.6,
              fontSize: '0.95rem',
            }}
          >
            {MOCK_AGREEMENT_CONTENT}
          </Typography>
        </ScrollableContent>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 3,
            p: 2,
            backgroundColor: 'background.default',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Checkbox
            checked={isAccepted}
            onChange={(_, checked) => handleAcceptanceChange(checked)}
            disabled={isLoading}
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
          <Typography
            variant='body1'
            sx={{
              fontWeight: 500,
              color: 'text.primary',
            }}
          >
            I have read and agree to the TransUnion Service Agreement terms and
            conditions.
          </Typography>
        </Box>
      </Box>
    </MultiStepLayout>
  );
};

export default AgreementStep;
